import {
  HOURS_PER_DAY,
  INSTALLMENT_BILLING,
  RECURRING_BILLING,
  WORKING_DAYS_PER_MONTH,
} from '../../constants';
import { contractTotalMonths } from '../../utils/dates';
import { uid } from '../../utils/format';
import { blankDealLine } from './dealLineDefaults';

const num = (v) => (v === '' || v == null ? '' : parseFloat(v));
const HOURS_PER_MONTH = HOURS_PER_DAY * WORKING_DAYS_PER_MONTH;

// Engagement d'un contrat d'après ses dates (un mois commencé compte) ; sans date de fin, la durée
// saisie (contrat ouvert, reconduit tacitement).
const engagementOf = (start, end, months) => contractTotalMonths(start, end) || num(months);

// Montants vendus d'une ligne, depuis les champs d'une phase saisie à la conversion.
function soldAmounts(ph, fact) {
  if (fact === 'forfait_brique' || fact === 'forfait_global')
    return {
      budget_vendu: num(ph.montant_global),
      jours_vendus: num(ph.jours_total),
      tjm_vendu: num(ph.tjm),
      tjm: num(ph.tjm),
    };
  if (fact === 'regie_temporelle')
    return { nb_devs_vendu: num(ph.nb_devs), tjm_vendu: num(ph.tjm) };
  // Régie horaire : la phase raisonne en jours (TJM = taux × 7 h, devs = heures ÷ 140 h).
  if (fact === 'regie_horaire')
    return {
      heures_vendu: ph.nb_devs
        ? Math.round(parseFloat(ph.nb_devs) * HOURS_PER_MONTH * 100) / 100
        : '',
      heures_unite_vendu: 'heures_mois',
      taux_horaire_vendu: ph.tjm
        ? Math.round((parseFloat(ph.tjm) / HOURS_PER_DAY) * 100) / 100
        : '',
    };
  return {
    montant_mensuel: num(ph.montant_global),
    correctif_qte: num(ph.correctif_qte),
    correctif_unite: ph.correctif_unite,
    evolutif_qte: num(ph.evolutif_qte),
    evolutif_unite: ph.evolutif_unite,
  };
}

/* Ligne vendue créée pour une phase ajoutée à la main à la conversion : le budget se pilote depuis
   le CRM, chaque phase doit donc avoir sa ligne (contrôlée comme les autres avant conversion). */
export function phaseDraftToLine(ph) {
  const fact = ph.facturation || 'forfait_brique';
  const recurring = RECURRING_BILLING.includes(fact);
  return {
    ...blankDealLine(),
    id: uid(),
    nom: ph.nom || '',
    facturation: fact,
    ...soldAmounts(ph, fact),
    date_debut_prevue: ph.date_debut || '',
    date_fin_prevue: ph.date_fin || '',
    engagement_mois_vendu: recurring ? engagementOf(ph.date_debut, ph.date_fin, ph.duree_mois) : '',
  };
}

/* Ligne d'origine avec les dates retenues pour sa phase : les dates se pilotent depuis le projet,
   l'engagement suit (recalculé depuis les dates quand la fin est connue). Sauf le forfait
   échelonné : ses dates sont l'échéancier de paiement, indépendant du chantier. */
export function lineWithPhaseDates(line, ph) {
  if (line.facturation === INSTALLMENT_BILLING) return line;
  const start = ph.date_debut || '',
    end = ph.date_fin || '';
  if (start === (line.date_debut_prevue || '') && end === (line.date_fin_prevue || '')) return line;
  const months = contractTotalMonths(start, end);
  return {
    ...line,
    date_debut_prevue: start,
    date_fin_prevue: end,
    ...(months ? { engagement_mois_vendu: months } : {}),
  };
}

/* Lignes de l'affaire et phases à créer, reliées une à une : une phase issue d'une ligne lui
   reporte ses dates, une phase ajoutée à la main reçoit une nouvelle ligne. */
export function linesForConversion(lignes, phases) {
  let lines = [...(lignes || [])];
  const linked = phases.map((ph) => {
    if (ph.ligne_id) {
      lines = lines.map((l) => (l.id === ph.ligne_id ? lineWithPhaseDates(l, ph) : l));
      return ph;
    }
    const line = phaseDraftToLine(ph);
    lines.push(line);
    return { ...ph, ligne_id: line.id };
  });
  return { lines, phases: linked };
}
